import { createContext, useMemo } from 'react';

import { useSafeContext } from '@aspendental/shared-utils/react';

import { TAGBrand, BrandThemeConfig } from '@/types';
import { default as baseConfig } from '@/theme/BaseBrandThemeConfig';
import { httpClient } from '@/utils/httpClient/httpClient';

export interface IAppContext {
	theme: TAGBrand;
	environment: EnvironmentType;
	config: BrandThemeConfig;
	appName: string;
	appVersion: string;
	dentrinoClientId?: string;
	dentrinoClientSecret?: string;
	stateCodeFromHeaders?: string;
	/**
	 * VWO `ccPatientPrismPhoneSwap` treatment. When true, `TAGButton`/`TAGLink`
	 * swap the national call-center number (href + label) for the Patient Prism
	 * pool number and suppress Invoca tagging on that number. Resolved by the
	 * consumer app from `serverFeatureFlags`; defaults to false.
	 */
	phoneSwapOn?: boolean;
}
export interface IAppContextProvider extends IAppContext {
	children: React.ReactNode;
}

const AppContext = createContext<IAppContext>({
	theme: 'AspenDental' as TAGBrand,
	environment: 'prod' as EnvironmentType,
	config: baseConfig,
	appName: 'tag_unknown',
	appVersion: 'unknown',
	dentrinoClientId: 'unknown',
	dentrinoClientSecret: 'unknown',
	stateCodeFromHeaders: '',
	phoneSwapOn: false,
});
AppContext.displayName = 'AppContext';

type EnvironmentType = 'local' | 'dev' | 'stage' | 'prod';

export function AppContextProvider({
	theme,
	environment,
	config,
	appName,
	appVersion,
	children,
	dentrinoClientId,
	dentrinoClientSecret,
	stateCodeFromHeaders,
	phoneSwapOn,
}: Readonly<IAppContextProvider>) {
	const appContext = useMemo(
		() => ({
			theme: theme,
			environment: environment,
			config: config,
			appName: appName,
			appVersion: appVersion,
			dentrinoClientId: dentrinoClientId ?? '',
			dentrinoClientSecret: dentrinoClientSecret ?? '',
			stateCodeFromHeaders: stateCodeFromHeaders ?? '',
			phoneSwapOn: phoneSwapOn ?? false,
		}),
		[
			theme,
			environment,
			config,
			appName,
			appVersion,
			dentrinoClientId,
			dentrinoClientSecret,
			stateCodeFromHeaders,
			phoneSwapOn,
		]
	);
	const revspringApiUrl =
		environment === 'prod' ? config.services.prod.REVSPRING_API_URL : config.services.nonprod.REVSPRING_API_URL;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
	httpClient.getHTTPClient().defaults.baseURL = revspringApiUrl;
	return <AppContext.Provider value={appContext}>{children}</AppContext.Provider>;
}

export function useAppContext() {
	return useSafeContext(AppContext);
}
